import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AuroraBackground from '../components/AuroraBackground';
import { decodeAccountId } from '../services/common';
import { usePostingQueueSubscription } from '../hooks/usePostingQueueSubscription';
import api, { PendingComment } from '../services/api';

const SWIPE_THRESHOLD = 100; // pixels to trigger mark-as-posted
const SLIDE_OUT_MS = 300;
const COLLAPSE_MS = 250;

const PostingQueuePage: React.FC = () => {
    const { accountId } = useParams<{ accountId: string }>();
    const navigate = useNavigate();
    const [startingPosting, setStartingPosting] = useState(false);
    const [pausingPosting, setPausingPosting] = useState(false);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [isAutoPostingFeatureActivated, setIsAutoPostingFeatureActivated] = useState(false);
    const [swipeState, setSwipeState] = useState<{ id: string; startX: number; currentX: number } | null>(null);
    const didSwipeRef = useRef(false);

    // Animation state: 'slide' = card sliding off right, 'collapse' = row height collapsing
    const [animatingId, setAnimatingId] = useState<string | null>(null);
    const [animPhase, setAnimPhase] = useState<'slide' | 'collapse' | null>(null);
    // Local display list that keeps the animating item even after Firestore removes it
    const [displayComments, setDisplayComments] = useState<PendingComment[]>([]);
    const animatingItemRef = useRef<PendingComment | null>(null);

    useEffect(() => {
        api.getFrontendConfig().then((config) => {
            setIsAutoPostingFeatureActivated(config.isAutoPostingFeatureActivated);
        }).catch((err) => {
            console.error('Failed to fetch frontend config:', err);
        });
    }, []);

    const username = decodeAccountId(accountId || '');
    const accountUrl = `https://www.instagram.com/${username}/`;

    const {
        pendingComments,
        combinedStatus,
        loading,
        refresh,
    } = usePostingQueueSubscription(accountId, {
        vmPollInterval: 30000,
        commentsLimit: 100,
    });

    // Sync displayComments from Firestore's pendingComments, but keep the animating item
    useEffect(() => {
        setDisplayComments(prev => {
            const currentAnimId = animatingItemRef.current?.id;
            if (!currentAnimId) return pendingComments;
            // If the animating item was removed by Firestore, re-insert it at its previous position
            if (!pendingComments.find(c => c.id === currentAnimId)) {
                const prevIndex = prev.findIndex(c => c.id === currentAnimId);
                const insertAt = prevIndex >= 0 ? prevIndex : 0;
                const newList = [...pendingComments];
                newList.splice(Math.min(insertAt, newList.length), 0, animatingItemRef.current!);
                return newList;
            }
            return pendingComments;
        });
    }, [pendingComments]);

    const isNeedsLogin = combinedStatus?.status === 'needs_login';
    const isVmViewable = (combinedStatus?.vmStatus === 'RUNNING' || combinedStatus?.status === 'needs_login') && !!combinedStatus?.vncUrl;
    const isQueueRunning = combinedStatus?.isPostingQueueRunning === true;

    const handleStartPosting = async () => {
        setStartingPosting(true);
        try {
            await api.startPostingQueue(accountUrl);

            const maxAttempts = 60;
            const pollInterval = 2000;

            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                try {
                    const status = await api.getPostingQueueStatus(accountId!, false);

                    if (status.isPostingQueueRunning === false) {
                        break;
                    }

                    if (status.vmStatus === 'RUNNING' && status.vncUrl) {
                        break;
                    }
                } catch (pollErr) {
                    console.error('Poll error:', pollErr);
                }

                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }

            refresh();
        } catch (err) {
            console.error('Failed to start posting:', err);
        } finally {
            setStartingPosting(false);
        }
    };

    const handlePausePosting = async () => {
        setPausingPosting(true);
        try {
            await api.stopPostingQueue(accountId!);

            const maxAttempts = 30;
            const pollInterval = 2000;

            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                try {
                    const status = await api.getPostingQueueStatus(accountId!, false);

                    if (status.isPostingQueueRunning === false) {
                        break;
                    }
                } catch (pollErr) {
                    console.error('Poll error:', pollErr);
                }

                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }

            refresh();
        } catch (err) {
            console.error('Failed to pause posting:', err);
        } finally {
            setPausingPosting(false);
        }
    };

    const handleViewVM = () => {
        if (combinedStatus?.vncUrl) {
            window.open(combinedStatus.vncUrl, '_blank');
        }
    };

    const handleMarkAsPosted = useCallback(async (commentId: string) => {
        if (!accountId || animatingId) return;

        // Save a reference to the item so we can keep it in displayComments during animation
        const item = displayComments.find(c => c.id === commentId);
        if (item) animatingItemRef.current = item;

        // Phase 1: slide the card off to the right
        setAnimatingId(commentId);
        setAnimPhase('slide');
        setSwipeState(null);

        // Fire the API call (don't await - let animation run independently)
        api.markCommentAsPosted(accountId, commentId).catch(err => {
            console.error('Failed to mark comment as posted:', err);
        });

        // After slide finishes, start collapse
        setTimeout(() => {
            setAnimPhase('collapse');

            // After collapse finishes, clean up
            setTimeout(() => {
                setAnimatingId(null);
                setAnimPhase(null);
                animatingItemRef.current = null;
                // Remove from display list (Firestore may have already removed it)
                setDisplayComments(prev => prev.filter(c => c.id !== commentId));
            }, COLLAPSE_MS);
        }, SLIDE_OUT_MS);
    }, [accountId, animatingId, displayComments]);

    const handlePointerDown = (commentId: string, clientX: number) => {
        didSwipeRef.current = false;
        setSwipeState({ id: commentId, startX: clientX, currentX: clientX });
    };

    const handlePointerMove = (clientX: number) => {
        if (!swipeState) return;
        const deltaX = clientX - swipeState.startX;
        // Only allow right swipe
        if (deltaX > 0) {
            if (deltaX > 10) didSwipeRef.current = true;
            setSwipeState({ ...swipeState, currentX: clientX });
        }
    };

    const handlePointerEnd = () => {
        if (!swipeState) return;
        const deltaX = swipeState.currentX - swipeState.startX;
        if (deltaX >= SWIPE_THRESHOLD) {
            handleMarkAsPosted(swipeState.id);
        } else {
            setSwipeState(null);
        }
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return 'Unknown';
        const date = new Date(dateStr);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return { bg: '#FEF3C7', color: '#92400E' };
            case 'posted':
                return { bg: '#D1FAE5', color: '#065F46' };
            default:
                return { bg: '#F3F4F6', color: '#374151' };
        }
    };

    if (loading) {
        return (
            <>
                <AuroraBackground />
                <div
                    style={{
                        minHeight: '100vh',
                        paddingTop: '72px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <div style={{ textAlign: 'center', color: '#666666' }}>
                        <div
                            style={{
                                width: '32px',
                                height: '32px',
                                border: '3px solid rgba(0, 0, 0, 0.1)',
                                borderTop: '3px solid #7C3AED',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto 16px',
                            }}
                        />
                        Loading posting queue...
                    </div>
                    <style>
                        {`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}
                    </style>
                </div>
            </>
        );
    }

    return (
        <>
            <AuroraBackground />
            <div
                style={{
                    minHeight: '100vh',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                }}
            >
                {/* Header */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    padding: '100px 24px 40px',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                }}>
                    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                        <button
                            onClick={() => navigate('/sessions')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px 0',
                                color: '#666666',
                                fontSize: '14px',
                                cursor: 'pointer',
                                marginBottom: '24px',
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                            Back to Sessions
                        </button>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '16px',
                        }}>
                            <div>
                                <h1 style={{
                                    fontSize: '32px',
                                    fontWeight: 500,
                                    color: '#000000',
                                    marginBottom: '4px',
                                    letterSpacing: '-0.02em',
                                }}>
                                    @{username}
                                </h1>
                                <div style={{ fontSize: '14px', color: '#666666' }}>
                                    Posting Queue - {pendingComments.length} item{pendingComments.length !== 1 ? 's' : ''}
                                </div>
                            </div>

                            {isAutoPostingFeatureActivated && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                    onClick={handleViewVM}
                                    disabled={!isVmViewable}
                                    style={{
                                        padding: '10px 20px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        background: isVmViewable ? '#7C3AED' : '#D1D5DB',
                                        color: '#FFFFFF',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: isVmViewable ? 'pointer' : 'not-allowed',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        opacity: isVmViewable ? 1 : 0.6,
                                    }}
                                    title={
                                        isVmViewable
                                            ? 'Open VM console'
                                            : combinedStatus?.vmStatus === 'SUSPENDED'
                                            ? 'VM is suspended'
                                            : 'VM not running'
                                    }
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                        <line x1="8" y1="21" x2="16" y2="21" />
                                        <line x1="12" y1="17" x2="12" y2="21" />
                                    </svg>
                                    View VM
                                </button>

                                {isQueueRunning ? (
                                    <button
                                        onClick={handlePausePosting}
                                        disabled={pausingPosting}
                                        style={{
                                            padding: '10px 20px',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            background: pausingPosting ? '#FCA5A5' : '#EF4444',
                                            color: '#FFFFFF',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: pausingPosting ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        {pausingPosting ? (
                                            <>
                                                <div
                                                    style={{
                                                        width: '14px',
                                                        height: '14px',
                                                        border: '2px solid #FFFFFF',
                                                        borderTop: '2px solid transparent',
                                                        borderRadius: '50%',
                                                        animation: 'spin 1s linear infinite',
                                                    }}
                                                />
                                                Pausing...
                                            </>
                                        ) : (
                                            <>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                    <rect x="6" y="4" width="4" height="16" />
                                                    <rect x="14" y="4" width="4" height="16" />
                                                </svg>
                                                Pause
                                            </>
                                        )}
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleStartPosting}
                                        disabled={startingPosting}
                                        style={{
                                            padding: '10px 20px',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            background: startingPosting ? '#FB923C' : '#F97316',
                                            color: '#FFFFFF',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: startingPosting ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        {startingPosting ? (
                                            <>
                                                <div
                                                    style={{
                                                        width: '14px',
                                                        height: '14px',
                                                        border: '2px solid #FFFFFF',
                                                        borderTop: '2px solid transparent',
                                                        borderRadius: '50%',
                                                        animation: 'spin 1s linear infinite',
                                                    }}
                                                />
                                                Starting...
                                            </>
                                        ) : (
                                            <>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                    <polygon points="5,3 19,12 5,21" />
                                                </svg>
                                                Start
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 24px' }}>
                    {/* Login Required Banner */}
                    {isAutoPostingFeatureActivated && isNeedsLogin && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px 20px',
                                marginBottom: '24px',
                                background: '#FEF3C7',
                                border: '1px solid #F59E0B',
                                borderRadius: '8px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2">
                                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <div>
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400E' }}>
                                        Login Required
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#B45309' }}>
                                        Please log in to Instagram via the VM console to continue posting.
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={handleViewVM}
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    background: '#F97316',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                    <line x1="8" y1="21" x2="16" y2="21" />
                                    <line x1="12" y1="17" x2="12" y2="21" />
                                </svg>
                                Login via VNC
                            </button>
                        </div>
                    )}

                    {/* Queue Items */}
                    <h2 style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#666666',
                        marginBottom: '16px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                    }}>
                        Pending Comments
                    </h2>

                    {displayComments.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 40px',
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                        }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                background: 'rgba(0, 0, 0, 0.05)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 24px',
                                color: '#999999',
                            }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                </svg>
                            </div>
                            <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#000000', marginBottom: '8px' }}>
                                No pending comments
                            </h3>
                            <p style={{ color: '#666666', fontSize: '15px' }}>
                                Accept comments from a session to add them to the posting queue.
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {displayComments.map((item, index) => {
                                const badge = getStatusBadge(item.status);
                                const isBeingSwiped = swipeState?.id === item.id;
                                const swipeDelta = isBeingSwiped ? Math.max(0, swipeState!.currentX - swipeState!.startX) : 0;
                                const swipeProgress = Math.min(swipeDelta / SWIPE_THRESHOLD, 1);
                                const isAnimating = animatingId === item.id;
                                const isSliding = isAnimating && animPhase === 'slide';
                                const isCollapsing = isAnimating && animPhase === 'collapse';
                                const isLast = index === displayComments.length - 1;
                                return (
                                    <div
                                        key={item.id}
                                        style={{
                                            overflow: 'hidden',
                                            marginBottom: isCollapsing ? 0 : (isLast ? 0 : 8),
                                            maxHeight: isCollapsing ? 0 : 500,
                                            opacity: isCollapsing ? 0 : 1,
                                            transition: isCollapsing
                                                ? `max-height ${COLLAPSE_MS}ms ease-out, margin-bottom ${COLLAPSE_MS}ms ease-out, opacity ${COLLAPSE_MS * 0.6}ms ease-out`
                                                : 'none',
                                        }}
                                    >
                                      <div
                                        style={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            borderRadius: '8px',
                                        }}
                                      >
                                        {/* Swipe background indicator */}
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            bottom: 0,
                                            width: '100%',
                                            background: (isSliding || swipeProgress >= 1) ? '#10B981' : '#D1FAE5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            paddingLeft: '20px',
                                            opacity: (isBeingSwiped || isSliding) ? 1 : 0,
                                            transition: (isBeingSwiped || isSliding) ? 'none' : 'opacity 0.2s',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={swipeProgress >= 1 || isSliding ? '#FFFFFF' : '#065F46'} strokeWidth="2">
                                                    <polyline points="20,6 9,17 4,12" />
                                                </svg>
                                                <span style={{
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    color: swipeProgress >= 1 || isSliding ? '#FFFFFF' : '#065F46',
                                                }}>
                                                    {isSliding ? 'Marked as posted!' : swipeProgress >= 1 ? 'Release to mark posted' : 'Swipe to mark posted'}
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            onTouchStart={(e) => handlePointerDown(item.id, e.touches[0].clientX)}
                                            onTouchMove={(e) => handlePointerMove(e.touches[0].clientX)}
                                            onTouchEnd={handlePointerEnd}
                                            onMouseDown={(e) => { e.preventDefault(); handlePointerDown(item.id, e.clientX); }}
                                            onMouseMove={(e) => { if (e.buttons === 1) handlePointerMove(e.clientX); }}
                                            onMouseUp={handlePointerEnd}
                                            onMouseLeave={() => { if (swipeState?.id === item.id) handlePointerEnd(); }}
                                            onClick={() => {
                                                if (didSwipeRef.current) return;
                                                navigator.clipboard.writeText(item.content).then(() => {
                                                    setCopiedId(item.id);
                                                    setTimeout(() => setCopiedId(null), 2000);
                                                }).catch(err => {
                                                    console.error('Failed to copy comment:', err);
                                                });
                                                window.open(item.postUrl, '_blank');
                                            }}
                                            disabled={isAnimating}
                                            style={{
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '16px 20px',
                                                background: 'rgba(255, 255, 255, 0.95)',
                                                backdropFilter: 'blur(10px)',
                                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                                borderRadius: '8px',
                                                cursor: isAnimating ? 'default' : 'pointer',
                                                transition: isBeingSwiped ? 'none' : `transform ${SLIDE_OUT_MS}ms ease-in-out`,
                                                transform: isSliding
                                                    ? 'translateX(110%)'
                                                    : isBeingSwiped
                                                    ? `translateX(${swipeDelta}px)`
                                                    : 'translateX(0)',
                                                position: 'relative',
                                            }}
                                            title="Open post on Instagram & copy comment to clipboard. Swipe right to mark as posted."
                                        >
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                marginBottom: '8px',
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '50%',
                                                            background: '#F59E0B',
                                                            color: '#FFFFFF',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {(item.username || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>
                                                            @{item.username || 'unknown'}
                                                        </div>
                                                        <a
                                                            href={item.postUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{
                                                                fontSize: '12px',
                                                                color: '#3B82F6',
                                                                textDecoration: 'none',
                                                            }}
                                                        >
                                                            Click to copy the comment and open the post on Instagram
                                                        </a>
                                                    </div>
                                                </div>
                                                <span
                                                    style={{
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        padding: '4px 10px',
                                                        borderRadius: '4px',
                                                        background: badge.bg,
                                                        color: badge.color,
                                                        textTransform: 'uppercase',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {item.status}
                                                </span>
                                            </div>
                                            <div style={{
                                                fontSize: '14px',
                                                color: '#333333',
                                                lineHeight: 1.5,
                                                marginBottom: '6px',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '8px',
                                            }}>
                                                <span style={{ flex: 1 }}>{item.content}</span>
                                                {copiedId === item.id && (
                                                    <span style={{
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        color: '#10B981',
                                                        whiteSpace: 'nowrap',
                                                        flexShrink: 0,
                                                        padding: '2px 8px',
                                                        background: '#D1FAE5',
                                                        borderRadius: '4px',
                                                    }}>
                                                        Copied!
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#999999' }}>
                                                Queued: {formatDate(item.queuedAt)}
                                            </div>
                                        </button>
                                      </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <style>
                    {`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}
                </style>
            </div>
        </>
    );
};

export default PostingQueuePage;
